import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  List, ListItem, ListItemText, ListItemButton, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Chip, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Delete, Search, Visibility, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function FbCampaigns() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [advertisers, setAdvertisers] = useState([]);
  const [availableAdvertisers, setAvailableAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogSearchTerm, setDialogSearchTerm] = useState('');
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [activeRes, availableRes] = await Promise.all([
        axios.get('/admin/facebook/advertisers'),
        axios.get('/admin/facebook/advertisers/available')
      ]);
      setAdvertisers(activeRes.data.data || []);
      setAvailableAdvertisers(availableRes.data.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdvertiser = async (advertiserId) => {
    try {
      await axios.post('/admin/facebook/advertisers', { advertiser_id: advertiserId });
      setMessage({ type: 'success', text: 'Advertiser added successfully' });
      setDialogOpen(false);
      setDialogSearchTerm('');
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add advertiser' });
    }
  };

  const handleRemoveAdvertiser = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this advertiser from Facebook section?')) return;
    try {
      await axios.delete(`/admin/facebook/advertisers/${id}`);
      setMessage({ type: 'success', text: 'Advertiser removed' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove advertiser' });
    }
  };

  const filteredAdvertisers = advertisers.filter(adv => {
    const matchesSearch = searchTerm === '' || 
      adv.advertiser_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const filteredAvailableAdvertisers = availableAdvertisers.filter(adv =>
    adv.name.toLowerCase().includes(dialogSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Facebook Advertisers
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)} size={isMobile ? 'small' : 'medium'}>
          Add Advertiser
        </Button>
      </Box>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
            <Tabs value={filter} onChange={(e, v) => setFilter(v)} sx={{ minHeight: { xs: 36, sm: 48 } }}>
              <Tab label="Active" value="active" sx={{ minHeight: { xs: 36, sm: 48 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
              <Tab label="Inactive" value="inactive" sx={{ minHeight: { xs: 36, sm: 48 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
            </Tabs>
            <TextField
              size="small"
              placeholder="Search advertisers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: { xs: '100%', sm: 250 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  {!isMobile && <TableCell>ID</TableCell>}
                  <TableCell>Name</TableCell>
                  <TableCell align="center">Campaigns</TableCell>
                  <TableCell align="center">Leads (This Month)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAdvertisers.length > 0 ? (
                  filteredAdvertisers.map((adv) => (
                    <TableRow 
                      key={adv.id} 
                      hover 
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/facebook/campaigns/${adv.advertiser_id}`)}
                    >
                      {!isMobile && <TableCell>{adv.advertiser_id}</TableCell>}
                      <TableCell>
                        <Typography
                          sx={{ color: 'primary.main', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' }, '&:hover': { textDecoration: 'underline' } }}
                        >
                          {adv.advertiser_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={adv.campaigns_count || 0} 
                          size="small" 
                          sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600 }} 
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={adv.month_leads || adv.total_leads || 0} 
                          size="small" 
                          sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600 }} 
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          onClick={(e) => { e.stopPropagation(); navigate(`/facebook/campaigns/${adv.advertiser_id}`); }} 
                          title="View Campaigns"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={(e) => handleRemoveAdvertiser(adv.id, e)} 
                          title="Remove"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 4 : 5} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {advertisers.length === 0 ? 'No advertisers added yet. Click "Add Advertiser" to get started.' : 'No advertisers found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Advertiser Dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setDialogSearchTerm(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add Advertiser</DialogTitle>
        <DialogContent>
          {availableAdvertisers.length === 0 ? (
            <Typography color="textSecondary" textAlign="center" py={4}>
              No available advertisers. All advertisers are already added.
            </Typography>
          ) : (
            <>
              <TextField
                fullWidth
                placeholder="Search advertisers..."
                value={dialogSearchTerm}
                onChange={(e) => setDialogSearchTerm(e.target.value)}
                sx={{ mb: 2, mt: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
              />
              {filteredAvailableAdvertisers.length === 0 ? (
                <Typography color="textSecondary" textAlign="center" py={2}>
                  No advertisers found matching "{dialogSearchTerm}"
                </Typography>
              ) : (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {filteredAvailableAdvertisers.map((adv) => (
                    <ListItem key={adv.advertiser_id} disablePadding>
                      <ListItemButton onClick={() => handleAddAdvertiser(adv.advertiser_id)}>
                        <ListItemText primary={adv.name} />
                        <Button size="small" variant="outlined">
                          Add
                        </Button>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setDialogSearchTerm(''); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FbCampaigns;
